import { z } from 'zod';
/**
 * Organization-level roles determining overall permissions within a provider organization
 * These roles follow a strict hierarchy: Owner > Manager > Staff
 */
export var ProviderUserRole;
(function (ProviderUserRole) {
    /**
     * Full administrative control over organization and all client accounts
     * - Can manage all users including other Owners
     * - Can create, modify, delete client accounts
     * - Has implicit access to all client accounts
     * - Can promote/demote any user to any role
     */
    ProviderUserRole["OWNER"] = "OWNER";
    /**
     * Nearly full control - implicit access to all client accounts, cannot manage Owners
     * - Can manage Staff and other Managers (but not Owners)
     * - Can create, modify, delete client accounts
     * - Has implicit access to all client accounts
     * - Cannot promote Staff to Owner role
     */
    ProviderUserRole["MANAGER"] = "MANAGER";
    /**
     * Basic access - must be explicitly granted access to specific client accounts
     * - Cannot manage other users or organization settings
     * - Must be explicitly granted access to each client account
     * - Has no implicit access to client accounts
     */
    ProviderUserRole["STAFF"] = "STAFF";
})(ProviderUserRole || (ProviderUserRole = {}));
export const ProviderUserRoleSchema = z.enum([
    ProviderUserRole.OWNER,
    ProviderUserRole.MANAGER,
    ProviderUserRole.STAFF,
]);
/**
 * Check if a role can manage another role
 * Owners can manage anyone; Managers can manage Staff and other Managers (but not Owners); Staff cannot manage anyone
 * Accepts both enum values and string literals for flexibility with generated API types
 */
export function canProviderUserRoleManage(managerRole, targetRole) {
    switch (managerRole) {
        case ProviderUserRole.OWNER:
            return true; // Owners can manage anyone
        case ProviderUserRole.MANAGER:
            return targetRole !== ProviderUserRole.OWNER; // Managers cannot manage Owners
        case ProviderUserRole.STAFF:
            return false; // Staff cannot manage anyone
        default:
            return false;
    }
}
/**
 * Check if a role can promote another role to a target role
 * Only Owners can promote to Owner role
 * Accepts both enum values and string literals for flexibility with generated API types
 */
export function canProviderUserRolePromoteTo(promoterRole, targetRole) {
    if (targetRole === ProviderUserRole.OWNER) {
        return promoterRole === ProviderUserRole.OWNER; // Only Owners can create Owners
    }
    return canProviderUserRoleManage(promoterRole, targetRole);
}
/**
 * Check if a role has implicit access to all client accounts
 * Owners and Managers have implicit access; Staff must be explicitly granted access
 * Accepts both enum values and string literals for flexibility with generated API types
 */
export function providerUserRoleHasImplicitClientAccountAccess(role) {
    return role === ProviderUserRole.OWNER || role === ProviderUserRole.MANAGER;
}
/**
 * Get all roles that are hierarchically below the given role
 */
export function getProviderUserRolesBelow(role) {
    switch (role) {
        case ProviderUserRole.OWNER:
            return [ProviderUserRole.MANAGER, ProviderUserRole.STAFF];
        case ProviderUserRole.MANAGER:
            return [ProviderUserRole.STAFF];
        case ProviderUserRole.STAFF:
            return [];
        default:
            return [];
    }
}
/**
 * Get the hierarchical level of a role (higher number = more permissions)
 */
export function getProviderUserRoleLevel(role) {
    switch (role) {
        case ProviderUserRole.OWNER:
            return 3;
        case ProviderUserRole.MANAGER:
            return 2;
        case ProviderUserRole.STAFF:
            return 1;
        default:
            return 0;
    }
}
//# sourceMappingURL=ProviderUserRole.js.map